import ActiveForm, {ActiveFormError} from './ActiveForm'
import {ValidatorFunction} from './Validators/ActiveFieldValidators'
import {CodesList} from './ActiveFormErrors'

export type ActiveFieldDefaultHandler = (scope: any, rootScope: any, formScope: any) => Promise<any> | any

export interface ActiveFieldOptions<InputOptions = any> {
  label?: string
  placeholder?: string
  name?: string
  required?: boolean
  readOnly?: boolean,
  hidden?: boolean,
  defaultValue?: any | ActiveFieldDefaultHandler
  validators?: ValidatorFunction[]
  type: any,
  options?: InputOptions
}

export class ActiveField<InputOptions = any> {
  public fieldName = 'field-name'
  public fieldPath = 'field-name'
  public fieldType = 'field-type'
  public options: ActiveFieldOptions<InputOptions>
  public form: ActiveForm
  public parent: ActiveField | undefined

  constructor (fieldName: string, fieldOptions: ActiveFieldOptions<InputOptions>, form: ActiveForm, parent: ActiveField | undefined) {
    this.fieldName = fieldName
    this.fieldPath = fieldName
    this.options = fieldOptions
    this.form = form
    this.parent = parent
  }

  public isRequired (): boolean {
    return !!this.options.required
  }

  public isEmpty (value: any): boolean {
    return !value
  }

  public cleanErrors () {
    this.form.cleanErrors(this.fieldPath)
  }

  public addError (code: keyof CodesList, message?: string) {
    this.form.addError({code, field: this.fieldPath, message})
  }

  public async validate (value: any, submit = false): Promise<boolean> {
    this.form.cleanErrors(this.fieldPath)
    let valid = true
    if (this.isRequired() && this.isEmpty(value)) {
      if (submit) {
        valid = false
        this.addError('required')
      }
    }
    if (!this.isEmpty(value)) {
      for (const validator of this.getValidators()) {
        try {
          if (Array.isArray(validator)) {
            for (const validatorChain of validator) {
              await validatorChain(this, value, submit)
            }
          } else {
            await validator(this, value, submit)
          }
        } catch (e) {
          e.field = this.fieldPath
          valid = false
          this.form.addError(e)
        }
      }
    }
    return valid
  }

  public getValidators () {
    return this.options.validators || []
  }

  public classes (className?: string | string[], global: boolean = false) {
    if (!className) {
      const classes = ['active-field', 'active-field--' + this.fieldType, 'active-field--' + this.fieldType + '--' + this.fieldName]
      if (this.form.fieldsHasErrors([this.fieldPath])) {
        classes.push('active-field--error')
        classes.push('active-field--' + this.fieldType + '--error')
        classes.push('active-field--' + this.fieldType + '--' + this.fieldName + '--error')
      }
      return classes
    }
    if (typeof className === 'string') {
      const classes = ['active-field--' + this.fieldType + '__' + className, 'active-field--' + this.fieldType + '__' + className + '--' + this.fieldName]
      if (global) {
        classes.push('active-field__' + className)
      }
      return classes
    }
    const classes = [] as string[]
    for (const classItem of className) {
      classes.push('active-field--' + this.fieldType + '__' + classItem)
      classes.push('active-field--' + this.fieldType + '__' + classItem + '--' + this.fieldName)
      if (global) {
        classes.push('active-field__' + classItem)
      }
    }
    return classes
  }

  public async initialValue (scope: any, rootScope: any, formScope: any) {
    const defaultValue = this.options.defaultValue
    if (typeof defaultValue === 'function') {
      return defaultValue(scope, rootScope, formScope)
    }
    return (typeof defaultValue !== 'undefined' && defaultValue) || ''
  }
}

export const fieldProps = {
  label: {type: String, default: ''},
  name: {type: String, default: ''},
  placeholder: {type: String, default: ''},
  required: {type: Boolean, default: false},
  readOnly: {type: Boolean, default: false},
  field: {type: Object as () => ActiveField},
  fieldOptions: {
    type: Object as () => any,
    default: () => {
      return {}
    }
  },
  value: {},
  defaultValue: {},
  error: {
    type: Array as () => ActiveFormError[],
    default: () => {
      return []
    }
  },
  scope: {type: Object as () => any},
  formScope: {type: Object as () => any},
  rootScope: {type: Object as () => any}
}